import { useState } from 'react'
import { usePetsittingMode } from '@/store/usePetsittingMode'
import { useTranslation } from 'next-i18next'
import { Box, Skeleton } from '@mui/material'
import { EmtAvatar, EmtButton, EmtCheckbox, EmtChip, EmtIcon, EmtSeparator, EmtTypography } from 'emt-components-v2'
import DeleteAnimalModal from './DeleteAnimalModal'
import AddAnimalModal from './AddAnimal/AddAnimalModal'
import { useEMTState } from '@/store'
import { AnimalData } from '@/components/PetSitter/AddAnimal/AnimalForm'

interface AnimalBoxProps {
  data:
    | (AnimalData & { uuid: string; avatar?: string; mainPictureUrl?: string; mainPictureThumbnailUrl?: string })
    | undefined
    | null
  onClick?: () => void
  isSelected?: boolean
  checkbox?: boolean
  color?: string
  edit?: (uuid: string) => void
  delete?: (uuid: string) => void
  onToggleVolunteer?: (uuid: string) => void
  showVolunteer?: boolean
  isLoading?: boolean
  isDeleting?: boolean
  disabled?: boolean
  added?: boolean
  removed?: boolean
  actionText?: () => void
}

const AnimalBox: React.FC<AnimalBoxProps> = ({
  data,
  onClick,
  isSelected = false,
  checkbox,
  edit,
  delete: deleteAnimal,
  color = 'purple',
  onToggleVolunteer,
  showVolunteer,
  isLoading,
  isDeleting = false,
  disabled = false,
  added = false,
  removed = false,
  actionText,
}) => {
  const { t } = useTranslation('petsitter')
  const { isPetsittingMode } = usePetsittingMode()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const { dogBreeds, catBreeds, nacBreeds } = useEMTState().configuration

  const isClickable = !!onClick && !disabled

  if (isLoading) {
    return (
      <Skeleton
        variant="rectangular"
        animation="wave"
        sx={{
          width: '100%',
          height: 70,
          borderRadius: '16px',
          padding: '14px 16px',
        }}
      />
    )
  }

  if (!data) {
    return null
  }
  const { breedSlug, speciesSlug, selectedRadio } = data

  const breed = breedSlug
    ? (speciesSlug || selectedRadio) === 'dog'
      ? dogBreeds.find((breed) => breed.id === breedSlug)
      : (speciesSlug || selectedRadio) === 'cat'
      ? catBreeds.find((breed) => breed.id === breedSlug)
      : (speciesSlug || selectedRadio) === 'nac'
      ? nacBreeds.find((breed) => breed.id === breedSlug)
      : undefined
    : undefined

  return (
    <Box
      onClick={isClickable ? onClick : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.()
              }
            }
          : undefined
      }
      role={isClickable ? 'checkbox' : undefined}
      aria-checked={isClickable ? isSelected : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-disabled={disabled || undefined}
      sx={{
        minWidth: 0,
        pointerEvents: disabled ? 'none' : 'auto',
        cursor: isClickable ? 'pointer' : 'default',
        '&:focus-visible > .animal-card': {
          outline: '2px solid #1D1929',
          outlineOffset: '2px',
        },
      }}
    >
      <DeleteAnimalModal open={deleteModalOpen} setOpen={setDeleteModalOpen} button uuid={data.uuid || ''} />
      <AddAnimalModal
        open={addModalOpen}
        close={() => setAddModalOpen(false)}
        defaultImages={isPetsittingMode}
        defaultDescription={isPetsittingMode}
      />
      <Box
        className="animal-card"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: '16px',
          padding: '12px',
          border: `1px solid ${added ? '#AE59C6' : '#E8E8EA'}`,
          backgroundColor: added ? '#FBF1FE' : removed ? 'transparent' : '#fff',
          borderRadius: '16px',
          opacity: disabled ? 0.5 : 1,
          filter: removed ? 'grayscale(1)' : 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '5px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <Box
              className="EmtInlineCard-image-wrapper"
              sx={{
                position: 'relative',
                width: '40px',
                height: '40px',
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <EmtAvatar
                sx={{ flexShrink: 0, height: '100%!important', width: '100%', borderRadius: '12px' }}
                size="large"
                src={
                  data?.mainPicture ||
                  data?.mainPictureUrl ||
                  data?.avatar ||
                  data?.mainPictureThumbnailUrl ||
                  undefined
                }
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
              }}
            >
              <EmtTypography
                variant="body1"
                fontWeight="700"
                sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', flexShrink: 1 }}
              >
                {data.displayName}
              </EmtTypography>
              <Box sx={{ display: 'flex', gap: '4px' }}>
                <EmtIcon
                  className={data.selectedRadio ? `emt-${data.selectedRadio}` : `emt-${data.speciesSlug}` || data.icon}
                  size="small"
                  sx={{
                    color: '#A5A3A9',
                  }}
                />
                <EmtTypography
                  variant="body2"
                  color="#A5A3A9"
                  sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                >
                  <span>{breed?.name}</span>
                </EmtTypography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {edit && (
              <EmtButton
                variant="contained"
                color={color}
                size="small"
                className="min-w-[unset] w-[34px] h-[34px] !p-0 flex-shrink-0"
                disabled={isDeleting}
                onClick={() => edit(data.uuid)}
              >
                <EmtIcon className="emt-edit" size="small" sx={{ fontSize: '15px' }} />
              </EmtButton>
            )}
            {deleteAnimal && (
              <EmtButton
                variant="outlined"
                color={color}
                size="small"
                className="min-w-[unset] w-[34px] h-[34px] !p-0 flex-shrink-0"
                loader={isDeleting}
                onClick={() => deleteAnimal(data.uuid)}
              >
                <EmtIcon className="emt-bin" size="small" />
              </EmtButton>
            )}
            {checkbox && (
              <EmtCheckbox
                sx={{
                  backgroundColor: '#F9F9F9',
                  '& .emt-icons': {
                    borderColor: '#D2D1D4',
                    width: '18px',
                    height: '18px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                }}
                checked={isSelected}
                size="medium"
                variant={color}
              />
            )}
          </Box>
          {removed && (
            <EmtChip label="Retiré" sx={{ backgroundColor: '#EBEBEC', color: '#77757F', alignSelf: 'flex-start' }} />
          )}
          {added && (
            <EmtChip label="Ajouté" sx={{ backgroundColor: '#ECD3F3', color: '#AE59C6', alignSelf: 'flex-start' }} />
          )}
          {actionText && (
            <EmtTypography
              variant="body2"
              color="#AE59C6"
              sx={{
                cursor: 'pointer',
                display: 'inline-block',
                textDecoration: 'underline',
              }}
              onClick={actionText}
            >
              Modifier
            </EmtTypography>
          )}
        </Box>
        {onToggleVolunteer && (
          <>
            <EmtSeparator />
            <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <EmtCheckbox
                sx={{
                  '& .emt-icons': {
                    width: '18px',
                    height: '18px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...(!showVolunteer && { borderColor: '#D2D1D4' }),
                  },
                }}
                checked={Boolean(data.showVolunteer)}
                size="medium"
                variant={color}
                onChange={(e) => {
                  e.stopPropagation()
                  onToggleVolunteer(data.uuid) // Tells the parent to flip showVolunteer
                }}
              />
              <Box sx={{ lineHeight: '1' }}>
                <EmtTypography variant="body2" fontWeight="700" lineHeight="1">
                  {t('animalBox.showVolunteerTitle')}
                </EmtTypography>
                <EmtTypography variant="caption" lineHeight="1" color="#77757F">
                  {t('animalBox.showVolunteerSubTitle')}
                </EmtTypography>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

export default AnimalBox
